import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { UserAuth } from "../../utils/AuthContext";
import useRequest from "../../utils/useRequest";

import classes from "../../components/Modal.module.css";

import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";

import CustomInput from "../../components/CustomInput";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import Loader from "../../components/Loader";
import AreYouSureModal from "../../components/AreYouSureModal";
import SectionDivider from "../../components/SectionDivider";
import ErrorBox from "../../components/ErrorBox";
import SubTreatmentsSection from "./SubTreatmentsSection";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const TreatmentInfoModal = ({ treatment, onClose, getAllTreatments }) => {
  const { t } = useTranslation("general");
  const [isEditing, setIsEditing] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");
  const [treatmentFields, setTreatmentFields] = useState({
    name: treatment?.name || "",
    price: treatment?.treatmentPrice || "",
    subMedicalTreatments:
      treatment?.subMedicalTreatment?.map((cat) => ({
        subTreatmentName: cat.name,
        subTreatmentPrice: cat.price,
      })) || [],
  });
  const [errorFields, setErrorFields] = useState({});
  const [showAreYouSure, setShowAreYouSure] = useState(false);
  const { userData } = useContext(UserAuth);

  const {
    success: deletedSuccessfully,
    loading: deleting,
    refetch: deleteTreatment,
  } = useRequest(
    `/deleteMedicalTreatment/${treatment.id}`,
    "DELETE",
    {},
    {},
    false
  );

  const handleInputChange = (e, fieldName) => {
    setTreatmentFields({
      ...treatmentFields,
      [fieldName]: e.target.value,
    });
  };

  const handleSubTreatmentChange = (subTreatments) => {
    setTreatmentFields({
      ...treatmentFields,
      subMedicalTreatments: subTreatments,
    });
  };

  const validateFields = () => {
    let errors = {};
    let isValid = true;

    if (!treatmentFields.name.trim()) {
      errors["treatmentName"] = t("emptyInputField");
      isValid = false;
    }

    if (treatmentFields.subMedicalTreatments.length === 0) {
      if (!treatmentFields.price || isNaN(parseFloat(treatmentFields.price))) {
        errors["treatmentPrice"] = t("validPriceRequired");
        isValid = false;
      }
    }

    treatmentFields.subMedicalTreatments.forEach((sub, index) => {
      if (!sub.subTreatmentName?.trim()) {
        errors[`subTreatmentName-${index}`] = t("subTreatmentNameRequired");
        isValid = false;
      }
      if (isNaN(parseFloat(sub.subTreatmentPrice))) {
        errors[`subTreatmentPrice-${index}`] = t("subTreatmentPriceRequired");
        isValid = false;
      }
    });

    setErrorFields(errors);
    return isValid;
  };

  const handleUpdateTreatment = async () => {
    if (!validateFields()) return;

    try {
      setLoadingModal(true);
      const token = userData?.token;
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/editMedicalTreatment/${treatment.id}`,
        {
          name: treatmentFields.name,
          treatmentPrice:
            treatmentFields.subMedicalTreatments.length === 0
              ? parseFloat(treatmentFields.price)
              : null,
          subMedicalTreatments: treatmentFields.subMedicalTreatments.map(
            (cat) => ({
              name: cat.subTreatmentName,
              price: parseFloat(cat.subTreatmentPrice),
            })
          ),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            id: treatment.id,
          },
        }
      );

      if (response.status) {
        getAllTreatments(1, "");
        onClose();
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update treatment");
    } finally {
      setLoadingModal(false);
    }
  };

  useEffect(() => {
    if (deletedSuccessfully) {
      setShowAreYouSure(false);
      getAllTreatments();
      onClose();
    }
  }, [deletedSuccessfully]);

  const handleDeleteTreatment = async () => deleteTreatment();

  return (
    <>
      <Modal
        title={isEditing ? t("editTreatment") : t("treatment")}
        onClose={onClose}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="o-row o-row--horizontal-align_end o-row--gap-size_300">
              {isEditing ? (
                <>
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setIsEditing(false)}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                  <CustomButton
                    onClick={handleUpdateTreatment}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                </>
              ) : (
                <>
                  {deleting ? <Loader /> : null}
                  <CustomButton
                    title={t("delete")}
                    variant="red"
                    icon={deleteIcon}
                    iconSize={20}
                    onClick={() => setShowAreYouSure(true)}
                    disabled={deleting}
                  />
                  <CustomButton
                    variant="blank"
                    title={t("edit")}
                    onClick={() => setIsEditing(true)}
                    icon={editIcon}
                    iconSize={18}
                    disabled={deleting}
                  />
                </>
              )}
            </section>
          </>
        }
      >
        <div className="o-column o-column--gap-size_100">
          {isEditing && error ? <ErrorBox>{t(error)}</ErrorBox> : null}

          {isEditing ? (
            <div className={classes.inputContainer}>
              <span>{t("treatmentName")}: </span>
              <CustomInput
                name="name"
                value={treatmentFields.name}
                onChange={(e) => handleInputChange(e, "name")}
                errorText={errorFields["treatmentName"]}
                flex={true}
              />
            </div>
          ) : (
            <div className="view-user-entity">
              <p>{t("treatmentName")}:</p>
              <span>{treatmentFields.name}</span>
            </div>
          )}

          {treatmentFields.subMedicalTreatments.length === 0 && (
            <>
              {isEditing ? (
                <div className={classes.inputContainer}>
                  <span>{t("price")}: </span>
                  <CustomInputWithUnit
                    name="price"
                    value={treatmentFields.price}
                    onChange={(e) => handleInputChange(e, "price")}
                    errorText={errorFields["treatmentPrice"]}
                    flex={true}
                    type="money"
                    unit={t("IQD")}
                  />
                </div>
              ) : (
                <div className="view-user-entity">
                  <p>{t("price")}:</p>
                  <span>{`${treatmentFields.price} ${t("IQD")}`}</span>
                </div>
              )}
            </>
          )}

          {isEditing && treatmentFields.subMedicalTreatments.length > 0 && (
            <SubTreatmentsSection
              sendDataToParent={handleSubTreatmentChange}
              errorFields={errorFields}
              validateFields={validateFields}
              initialSubTreatments={treatmentFields.subMedicalTreatments}
            />
          )}

          {!isEditing && treatmentFields.subMedicalTreatments.length > 0 && (
            <>
              <SectionDivider title={t("subTreatments")} />
              <section className="o-column o-column--gap-size_500">
                {treatmentFields.subMedicalTreatments.map((sub, index) => (
                  <div key={index}>
                    <div className="view-user-entity">
                      <p>{t("subTreatmentName")}:</p>
                      <span>{sub.subTreatmentName}</span>
                    </div>
                    <div className="view-user-entity">
                      <p>{t("subTreatmentPrice")}:</p>
                      <span>{`${sub.subTreatmentPrice} ${t("IQD")}`}</span>
                    </div>
                  </div>
                ))}
              </section>
            </>
          )}
        </div>
      </Modal>

      {showAreYouSure ? (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleDeleteTreatment}
          customMessage="areYouSureYouWantToDelete"
          closeTitle="noDontDelete"
          confirmTitle="yesDelete"
          loading={deleting}
        />
      ) : null}
    </>
  );
};

export default TreatmentInfoModal;
